import { useState, useEffect } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import Row from '@sdk/components/ui/spacing/Row';
import H4 from '@sdk/components/ui/typography/H4';
import gettext, { ngettext } from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Column from '@sdk/components/ui/spacing/Column';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import IconButton from '@sdk/components/ui/buttons/IconButton';
import Table, { TableHeader, TableHeaderCell, TableRow, TableCell } from '@sdk/components/ui/table';
import useOrderedData from '@sdk/utils/hooks/useOrderedData';
import useSlingApi from '@sdk/api/useSlingApi';
import {
  UnavailabilitySetApiType,
  UnavailabilitySetDisplayType,
} from '@sdk/unavailabilities/types/UnavailabilitySetType';
import { UNAVAILABILITY_INTERVALS } from '@sdk/unavailabilities/constants';
import MoreOptionsMenu from '@sdk/components/ui/MoreOptionsMenu';
import getUnavailabilitySetDisplayData from '@sdk/unavailabilities/helpers/getUnavailabilitySetDisplayData';
import getUnavailabilitySetListOptions, {
  UnavailabilitySetListOption,
} from '@sdk/unavailabilities/helpers/getUnavailabilitySetListOptions';
import StatusLabel, { StatusLabelType } from '@sdk/components/ui/StatusLabel';
import Spinner from '@sdk/components/ui/Spinner';
import { isApprovalEnabled, isHistorical } from '@sdk/unavailabilities/helpers/utils';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import { PickerOption } from '@sdk/components/forms/types';
import Text14 from '@sdk/components/ui/typography/Text14';
import ConfirmModal from '@sdk/components/ui/confirmModal/ConfirmModal';
import Href from '@sdk/components/ui/Href';

import WarningIcon from '../../assets/svg/Icons/32/announcement-red.svg';
import emptySetsImage from '../../../src/assets/svg/UI/unavailability-empty-sets.svg';
import getEmptyScreenTitle from '../helpers/getEmptyScreenTitle';
import PlusIcon from '../../assets/svg/UI/plus-white.svg';

const TopSummaryBg = styled.div`
  background-color: ${(props): string => props.theme.colors.columnHeaders};
  border-bottom: 1px solid ${({ theme }): string => theme.colors.separatorOnGray};
`;

const TopSummaryRow = styled(Row)`
  align-items: center;
  justify-content: space-between;
`;

const AddUnavailabilityButton = styled(IconButton)`
  flex: 0;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    /* IE10+ CSS styles go here */
    flex-grow: 1;
    flex-shrink: 0;
    flex-basis: auto;
    width: 170px;
    max-width: 170px;
  }
`;

const HistoryUrlContainer = styled(Column)`
  align-self: flex-end;
`;

const EmptyStateContainer = styled(Column)`
  margin: 50px auto 0;
  text-align: center;
  font-weight: 600;
`;

const EmptyStateImg = styled('img')`
  height: 140px;
  width: auto;
  margin-bottom: 20px;
`;

type Props = {
  currentUser: AuthUserType;
  isHistory?: boolean;
  goToSet: (param: {
    set: UnavailabilitySetDisplayType;
    unavailabilityCount?: number;
    editSet?: boolean;
  }) => void;
  goToNewSet: (param: { unavailabilityCount?: number }) => void;
  goToHistory: () => void;
  srcUserId?: number;
  approveSet: ({ set }: { set: UnavailabilitySetDisplayType }) => Promise<UnavailabilitySetApiType>;
  denySet: ({ set }: { set: UnavailabilitySetDisplayType }) => Promise<void>;
  canManage: boolean;
};

const UnavailabilityListScreen: React.FC<React.PropsWithChildren<Props>> = ({
  currentUser,
  isHistory,
  goToSet,
  goToNewSet,
  srcUserId,
  goToHistory,
  approveSet,
  denySet,
  canManage,
}) => {
  const [displayData, setDisplayData] = useState<UnavailabilitySetDisplayType[]>(null);
  const [historyCount, setHistoryCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirmSet, setDeleteConfirmSet] = useState<UnavailabilitySetDisplayType>(null);
  const viewingCurrentUser = srcUserId === currentUser.id || !srcUserId;
  const readOnly =
    (!currentUser.isManager && (!currentUser.orgSettings.usersSetAvailability || isHistory)) ||
    (currentUser.isManager && !canManage);

  const api = useSlingApi();
  const { isDescending, changeOrder, orderProp, orderedData } = useOrderedData(displayData);

  const deny = async (set: UnavailabilitySetDisplayType): Promise<void> => {
    await denySet({ set });
    const updatedData = displayData.filter((s) => s.id !== set.id);
    setDisplayData(updatedData);
  };

  const approve = async (set: UnavailabilitySetDisplayType): Promise<void> => {
    const updatedSet = await approveSet({ set });
    const updateIndex = displayData.findIndex((s) => s.id === set.id);

    if (updateIndex > -1) {
      const updatedData = [...displayData];
      updatedData[updateIndex] = getUnavailabilitySetDisplayData(updatedSet);
      setDisplayData(updatedData);
    }
  };

  const onOptionClick = (set: UnavailabilitySetDisplayType, option: PickerOption): void => {
    if (option.value === UnavailabilitySetListOption.edit) {
      goToSet({ set, unavailabilityCount: displayData.length, editSet: true });
    }

    if (option.value === UnavailabilitySetListOption.delete) {
      setDeleteConfirmSet(set);
    }

    if (option.value === UnavailabilitySetListOption.approve) {
      approve(set);
    }

    if (option.value === UnavailabilitySetListOption.deny) {
      deny(set);
    }
  };

  const deleteUnavailability = async (id: number): Promise<void> => {
    await api.unavailability.deleteUnavailability(deleteConfirmSet.id);
    const updatedData = displayData.filter((s) => s.id !== id);
    setDisplayData(updatedData);
  };

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      setIsLoading(true);
      const res = await api.unavailability.getUnavailabilitySets(srcUserId);
      const allSets = res.data?.map((s) => getUnavailabilitySetDisplayData(s));
      const currentSets = allSets.filter((s) => (isHistory ? isHistorical(s) : !isHistorical(s)));
      setDisplayData(currentSets);

      if (!isHistory) {
        const historicalSetsCount = allSets.length - currentSets.length;
        if (historicalSetsCount) {
          setHistoryCount(historicalSetsCount);
        }
      }

      setIsLoading(false);
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Column>
      {!!deleteConfirmSet && (
        <ConfirmModal
          fullScreen
          zIndex={2000}
          titleIcon={<WarningIcon />}
          titleThemeColor={ThemeColorType.danger}
          primaryButtonText={gettext('Delete')}
          dismiss={(): void => {
            setDeleteConfirmSet(null);
          }}
          onPrimaryClick={(): Promise<any> => deleteUnavailability(deleteConfirmSet.id)}
          primaryButtonType={ButtonType.danger}>
          <Text14>{gettext('Are you sure you want to delete this availability set?')}</Text14>
        </ConfirmModal>
      )}
      {!isHistory && (
        <TopSummaryBg>
          <TopSummaryRow mRight={20} mLeft={20}>
            <H4 themeColor={ThemeColorType.primary} mTop={26} mRight={20} mBottom={26}>
              {orderedData ? `${displayData.length} ${gettext('Unavailability')}` : ' '}
            </H4>

            {!readOnly && (
              <AddUnavailabilityButton
                buttonType={ButtonType.primary}
                iconRight={<PlusIcon />}
                onClick={(): void => goToNewSet({ unavailabilityCount: displayData.length })}>
                {gettext('Add new set')}
              </AddUnavailabilityButton>
            )}
          </TopSummaryRow>
        </TopSummaryBg>
      )}

      {!isHistorical && displayData?.length === 0 && (
        <Column mTop={20} mLeft={20}>
          <Text14>{gettext(getEmptyScreenTitle(readOnly, viewingCurrentUser))}</Text14>
        </Column>
      )}

      {!!displayData?.length && (
        <Table>
          <TableHeader>
            <TableHeaderCell
              label={gettext('Name')}
              orderProp="name"
              changeOrder={changeOrder}
              isDescending={isDescending}
              currentOrderProp={orderProp}
            />
            {/* Will be restored with single day unavailability
            <TableHeaderCell
              label={gettext('Type')}
              orderProp="displayType"
              changeOrder={changeOrder}
              isDescending={isDescending}
              currentOrderProp={orderProp}
            /> */}
            <TableHeaderCell
              label={gettext('Date')}
              orderProp="start"
              changeOrder={changeOrder}
              isDescending={isDescending}
              currentOrderProp={orderProp}
            />
            <TableHeaderCell
              label={gettext('Repeat')}
              orderProp="interval"
              changeOrder={changeOrder}
              isDescending={isDescending}
              currentOrderProp={orderProp}
            />
            {isApprovalEnabled(currentUser.orgSettings) && (
              <TableHeaderCell
                label={gettext('Status')}
                orderProp="isApproved"
                changeOrder={changeOrder}
                isDescending={isDescending}
                currentOrderProp={orderProp}
              />
            )}
            {!readOnly && <TableHeaderCell label={gettext('Options')} alignRight width={80} />}
          </TableHeader>
          <tbody>
            {orderedData?.map((set) => {
              return (
                <TableRow
                  wholeClickable
                  key={set.id}
                  onClick={(): void => goToSet({ set, unavailabilityCount: displayData.length })}>
                  <TableCell>{set.name}</TableCell>
                  {/* <TableCell>{gettext(set.displayType)}</TableCell> */}
                  <TableCell>{set.displayDate}</TableCell>
                  <TableCell>
                    {set.interval
                      ? gettext(
                          UNAVAILABILITY_INTERVALS.find((i) => i.value === set.interval).label
                        )
                      : '-'}
                  </TableCell>
                  {isApprovalEnabled(currentUser.orgSettings) && (
                    <TableCell pTop={0} pBottom={0}>
                      <StatusLabel
                        type={set.isApproved ? StatusLabelType.success : StatusLabelType.warning}
                        label={set.isApproved ? gettext('approved') : gettext('pending')}
                      />
                    </TableCell>
                  )}
                  {!readOnly && (
                    <TableCell pTop={0} pBottom={0} alignRight>
                      <MoreOptionsMenu
                        options={getUnavailabilitySetListOptions(set, currentUser)}
                        onClick={(option): void => onOptionClick(set, option)}
                      />
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      )}
      {isLoading && <Spinner pTop={12} pBottom={12} />}
      {!!historyCount && (
        <HistoryUrlContainer mTop={20} mRight={20} mBottom={20} mLeft={20}>
          <Href themeColor={ThemeColorType.primary} fontSize={14} onClick={goToHistory}>
            {ngettext(
              '%(historyCount)s more historical unavailability',
              '%(historyCount)s more historical unavailability',
              historyCount,
              { historyCount }
            )}
          </Href>
        </HistoryUrlContainer>
      )}
      {!isHistory && !(displayData && displayData.length) && !isLoading && (
        <EmptyStateContainer>
          <div>
            <EmptyStateImg src={emptySetsImage} />
          </div>
          <div>{gettext('No unavailability submitted yet.')}</div>
          {viewingCurrentUser ? (
            <div>{gettext('Add your unavailability to avoid scheduling conflicts.')}</div>
          ) : (
            <div>{gettext("Add employee's unavailability to avoid scheduling conflicts.")}</div>
          )}
        </EmptyStateContainer>
      )}
    </Column>
  );
};

export default UnavailabilityListScreen;
